import { Suspense, useEffect, useRef, type MutableRefObject, type ReactNode } from 'react';

interface Seen {
  /** The fallback was on screen. */
  shown: boolean;
  /** Route focus (useRouteFocus) put the focus on the fallback's heading. */
  headingFocused: boolean;
}

function Shown({ seen, children }: { seen: MutableRefObject<Seen>; children: ReactNode }) {
  useEffect(() => {
    seen.current = { shown: true, headingFocused: false };
    const onFocus = (event: FocusEvent) => {
      if (event.target instanceof HTMLHeadingElement && event.target.tagName === 'H1')
        seen.current.headingFocused = true;
    };
    document.addEventListener('focusin', onFocus);
    return () => document.removeEventListener('focusin', onFocus);
  }, [seen]);
  return <>{children}</>;
}

/**
 * After a route's code arrives: the document title follows the page's own
 * heading, and if route focus had put the focus on the skeleton's heading
 * (a navigation, ADR 126), it moves to the page's heading. On the first load
 * nothing had the focus, and nothing takes it.
 */
function Arrived({ seen, product }: { seen: MutableRefObject<Seen>; product: string }) {
  useEffect(() => {
    if (!seen.current.shown) return;
    const { headingFocused } = seen.current;
    seen.current = { shown: false, headingFocused: false };
    const h1 = document.querySelector<HTMLHeadingElement>('main h1');
    const page = h1?.textContent?.trim();
    if (page) document.title = `${page} · ${product}`;
    const lost = document.activeElement === null || document.activeElement === document.body;
    if (h1 && headingFocused && lost) {
      if (!h1.hasAttribute('tabindex')) h1.setAttribute('tabindex', '-1');
      h1.focus({ preventScroll: true });
    }
  });
  return null;
}

/**
 * The Suspense boundary of a lazy route (plan 45 phase 2): `fallback` is the
 * route's skeleton (design-system `RouteSkeleton`), at the size of the page.
 */
export function LazyRoute({
  fallback,
  product,
  children,
}: {
  fallback: ReactNode;
  /** Product name for the document title, e.g. "Laura". */
  product: string;
  children: ReactNode;
}) {
  const seen = useRef<Seen>({ shown: false, headingFocused: false });
  return (
    <Suspense fallback={<Shown seen={seen}>{fallback}</Shown>}>
      {children}
      <Arrived seen={seen} product={product} />
    </Suspense>
  );
}
